import axios from "axios";
import { getDeviceFingerprint } from "./deviceFingerprint";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    timeout: 60000,
});

// Stamp a stable device fingerprint on every outgoing request. Backend
// stores it alongside IP for security-signal forensics. MAC/IMEI are not
// accessible from the browser by design — this is the best substitute.
api.interceptors.request.use((config) => {
    try {
        config.headers["x-device-fingerprint"] = getDeviceFingerprint();
    } catch (_) { /* ignore — never block a request on fingerprint failure */ }
    return config;
});

// ---------------------------------------------------------------------------
// Silent access-token refresh on 401
// ---------------------------------------------------------------------------
// The backend issues a short-lived access_token (default 30 min) and a
// long-lived refresh_token (7 days) as HttpOnly cookies. When the access
// cookie expires the browser drops it and any subsequent API call returns
// 401 "Not authenticated". Instead of forcing a full sign-in, we transparently
// POST /api/auth/refresh once, then retry the original request. Requests
// during an in-flight refresh queue on the same promise so we never fire two
// concurrent /refresh calls (thundering-herd protection).
//
// If the refresh itself fails (revoked / user deleted / refresh cookie also
// expired), we blow away any client-side session state and redirect to
// /login?next=<current-path>.
// ---------------------------------------------------------------------------
let _refreshInFlight = null;               // Promise or null
const AUTH_SKIP_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/register", "/auth/otp", "/auth/me"];

function _isAuthSkip(url = "") {
    return AUTH_SKIP_PATHS.some(p => url.includes(p));
}

async function _doRefresh() {
    if (_refreshInFlight) return _refreshInFlight;
    _refreshInFlight = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
        .finally(() => { _refreshInFlight = null; });
    return _refreshInFlight;
}

api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const original = error?.config || {};
        const status = error?.response?.status;
        const url = original?.url || "";
        // Only auto-refresh on 401 for non-auth endpoints, and only once per request
        if (status !== 401 || _isAuthSkip(url) || original._retried) {
            return Promise.reject(error);
        }
        original._retried = true;
        try {
            await _doRefresh();
            return api(original);
        } catch (refreshErr) {
            // Refresh failed → hard sign-out. Preserve the current route so
            // the login form can bounce back after re-auth.
            try {
                const path = window.location.pathname + window.location.search;
                const isOnLogin = window.location.pathname.startsWith("/login")
                    || window.location.pathname.startsWith("/reset-password")
                    || window.location.pathname.startsWith("/forgot-password")
                    || window.location.pathname.startsWith("/partner/login")
                    || window.location.pathname.startsWith("/partner/register");
                if (!isOnLogin) {
                    const next = encodeURIComponent(path);
                    window.location.assign(`/login?next=${next}&reason=session_expired`);
                }
            } catch (_) { /* jsdom or SSR — swallow */ }
            return Promise.reject(refreshErr);
        }
    }
);

export function apiError(e) {
    const d = e?.response?.data?.detail;
    if (!d) return e?.message || "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
    return JSON.stringify(d);
}
